import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import PopUp from "./PopUp";
import UserIcon from "../icons/UserIcon";
import { AppContext } from "../App";
import functions from "../functions/functions";
import moment from "moment/moment";
import "moment/locale/it";
import Dish from "../icons/Dish";
import api from "./api/api";
import MergeIcon from "../icons/MergeIcon";
import PlanIcon from "../icons/PlanIcon";
import SidebarContent from "./SidebarContent";
import TrashIcon from "../icons/TrashIcon";
import ForkIcon from "../icons/ForkIcon";
import KeyIcon from "../icons/KeyIcon";
moment.locale("it");

const MAP_FALLBACK_GRID_ROWS = 8;
const MAP_GRID_MAX_COLUMNS = 9;
const MAP_GRID_MARGIN = 1.5;
const MAP_GRID_GAP = 1.5;
const MAP_TABLE_WIDTH_PERCENT =
    (100 - 2 * MAP_GRID_MARGIN - (MAP_GRID_MAX_COLUMNS - 1) * MAP_GRID_GAP) / MAP_GRID_MAX_COLUMNS;
const MAP_FALLBACK_GRID_LEFT_START = MAP_GRID_MARGIN;
const MAP_FALLBACK_GRID_LEFT_GAP = MAP_TABLE_WIDTH_PERCENT + MAP_GRID_GAP;
const MAP_FALLBACK_GRID_TOP_START = 2;
const MAP_FALLBACK_GRID_TOP_GAP = 12;
const MAP_FALLBACK_GRID_ROW_HEIGHT = 65;
const MAP_FALLBACK_GRID_MIN_HEIGHT = MAP_FALLBACK_GRID_ROWS * MAP_FALLBACK_GRID_ROW_HEIGHT;
const MAP_FALLBACK_GRID_TABLE_HEIGHT = 9;

function hasMapCoordinate(value) {
    return value !== null && value !== undefined && String(value).trim() !== "";
}

function hasMapPosition(table) {
    return hasMapCoordinate(table.pos_x) && hasMapCoordinate(table.pos_y);
}

function hasValidMapPosition(table) {
    if (!hasMapPosition(table)) return false;
    const left = getMapPercent(table.pos_x);
    const top = getMapPercent(table.pos_y);
    const width = MAP_TABLE_WIDTH_PERCENT;
    if (left < 0 || top < 0) return false;
    if (left + width > 100) return false;
    if (top + MAP_FALLBACK_GRID_TABLE_HEIGHT > 100) return false;
    return true;
}

function getMapPercent(value) {
    const percent = parseFloat(value);
    return Number.isFinite(percent) ? percent : 0;
}

function getGridColumn(left) {
    return Math.max(
        0,
        Math.round((getMapPercent(left) - MAP_FALLBACK_GRID_LEFT_START) / MAP_FALLBACK_GRID_LEFT_GAP),
    );
}

function getGridRow(top) {
    return Math.max(
        0,
        Math.round((getMapPercent(top) - MAP_FALLBACK_GRID_TOP_START) / MAP_FALLBACK_GRID_TOP_GAP),
    );
}

function getGridCellPosition(col, row) {
    return {
        left: MAP_FALLBACK_GRID_LEFT_START + col * MAP_FALLBACK_GRID_LEFT_GAP + "%",
        top: MAP_FALLBACK_GRID_TOP_START + row * MAP_FALLBACK_GRID_TOP_GAP + "%",
    };
}

function snapToGridCell(left, top) {
    return getGridCellPosition(getGridColumn(left), getGridRow(top));
}

function getTableMapPosition(table, mapPositionOverrides = {}) {
    const override = mapPositionOverrides[table.id];
    if (override) return override;
    if (!hasValidMapPosition(table)) return null;

    return snapToGridCell(table.pos_x, table.pos_y);
}

function getFallbackPositionMap(tables, mapPositionOverrides = {}) {
    const occupiedCells = new Set();
    const fallbackPositions = {};

    tables.forEach((table) => {
        const position = getTableMapPosition(table, mapPositionOverrides);
        if (position) occupiedCells.add(getGridColumn(position.left) + "," + getGridRow(position.top));
    });

    const missingTables = tables.filter((table) => !getTableMapPosition(table, mapPositionOverrides));

    let col = 0;
    let row = 0;

    missingTables.forEach((table) => {
        while (occupiedCells.has(col + "," + row)) {
            col++;
            if (col >= MAP_GRID_MAX_COLUMNS) {
                col = 0;
                row++;
            }
        }

        fallbackPositions[table.id] = getGridCellPosition(col, row);
        occupiedCells.add(col + "," + row);

        col++;
        if (col >= MAP_GRID_MAX_COLUMNS) {
            col = 0;
            row++;
        }
    });

    return fallbackPositions;
}

function TablesList() {
    const { tables, rooms, setLoading, setTablesListVisible, table, setTable, shop } = useContext(AppContext);
    const [room, setRoom] = useState(rooms.length > 0 ? rooms[0] : false);
    const [editMode, setEditMode] = useState(false);
    const [selectionEnabled, setSelectionEnabled] = useState(false);
    const [selectionType, setSelectionType] = useState(false);
    const [mapMode, setMapMode] = useState(
        localStorage.getItem("pos-map-mode") ? localStorage.getItem("pos-map-mode") == "true" : true,
    );
    const [selectedTables, setSelectedTables] = useState([]);
    const [srcParam, setSrcParam] = useState("");
    const [filteredTables, setFilteredTables] = useState([]);
    const [mapPositionOverrides, setMapPositionOverrides] = useState({});

    useEffect(() => {
        if (!selectionEnabled && selectedTables.length > 1) {
            if (selectionType == "merge_tables") mergeTables(selectedTables);
            if (selectionType == "parent_tables") parentTables(selectedTables);
            setSelectionType(false);
        }
        setSelectedTables([]);
    }, [selectionEnabled]);

    useEffect(() => {
        if (table) setTable(tables.find((t) => t.id == table.id));
    }, [tables]);

    useEffect(() => {
        if (!srcParam) {
            setFilteredTables(tables.filter((t) => t.shop_id == shop.id && t.room_id == room.id));
        } else {
            setFilteredTables(
                tables.filter(
                    (t) =>
                        t.shop_id == shop.id &&
                        t.room_id == room.id &&
                        t.name.toLowerCase().search(srcParam.toLowerCase()) > -1,
                ),
            );
        }
    }, [tables, room, srcParam]);

    useEffect(() => {
        localStorage.setItem("pos-map-mode", mapMode);
    }, [mapMode]);

    useEffect(() => {
        setTable(false);
    }, [room]);

    const mergeTables = async () => {
        console.log("mergeTables", selectedTables);
        setLoading(true);
        const res = await api.postProtected("/tables/merge_tables/", {
            tables_ids: selectedTables,
        });
        setLoading(false);
        if (res.success == 0) alert(res.error);
    };

    const parentTables = async () => {
        console.log("parentTables", selectedTables);
        setLoading(true);
        const res = await api.postProtected("/tables/parent_tables/", {
            tables_ids: selectedTables,
        });
        setLoading(false);
        if (res.success == 0) alert(res.error);
    };

    const enableEditMode = () => {
        setEditMode(true);
    };

    return (
        <PopUp
            size="full"
            options={{
                onClose: setTablesListVisible,
                title: "Tavoli",
                headerButtons: (
                    <>
                        {mapMode && !editMode && <button onClick={enableEditMode}>Modifica tavoli</button>}
                        {mapMode && editMode && (
                            <button onClick={() => setEditMode(false)}>Termina modifica</button>
                        )}
                        {!mapMode ? (
                            <button onClick={() => setMapMode(true)} className="icon">
                                <PlanIcon />
                                Abilita vista mappa
                            </button>
                        ) : (
                            <button onClick={() => setMapMode(false)} className="icon">
                                <PlanIcon />
                                Abilita vista elenco
                            </button>
                        )}
                        {selectionEnabled && selectionType == "merge_tables" ? (
                            <button onClick={() => setSelectionEnabled(false)} className="icon">
                                <MergeIcon /> Conferma selezione
                            </button>
                        ) : (
                            <button
                                onClick={() => {
                                    setSelectionEnabled(true);
                                    setSelectionType("merge_tables");
                                }}
                                className="icon"
                            >
                                <MergeIcon /> Unisci conti
                            </button>
                        )}
                        {selectionEnabled && selectionType == "parent_tables" ? (
                            <button onClick={() => setSelectionEnabled(false)} className="icon">
                                <MergeIcon /> Conferma selezione
                            </button>
                        ) : (
                            <button
                                onClick={() => {
                                    setSelectionEnabled(true);
                                    setSelectionType("parent_tables");
                                }}
                                className="icon"
                            >
                                <MergeIcon /> Unisci tavoli
                            </button>
                        )}
                    </>
                ),
                content: (
                    <div className="tables-list">
                        <div className="subheader">
                            <div className="rooms slim-scroll">
                                {rooms.map((r, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setRoom(r)}
                                        className={r.id == room.id ? "active" : ""}
                                    >
                                        {r.name}
                                    </button>
                                ))}
                            </div>
                            <div className="src-group">
                                {srcParam && (
                                    <button className="button square" onClick={(e) => setSrcParam("")}>
                                        X
                                    </button>
                                )}
                                <input
                                    type="text"
                                    value={srcParam}
                                    onChange={(e) => setSrcParam(e.target.value)}
                                    placeholder="Ricerca"
                                />
                            </div>
                        </div>
                        {!mapMode && (
                            <ListMode
                                selectedTables={selectedTables}
                                setSelectedTables={setSelectedTables}
                                selectionEnabled={selectionEnabled}
                                filteredTables={filteredTables}
                            />
                        )}
                        {mapMode && (
                            <MapMode2
                                selectedTables={selectedTables}
                                setSelectedTables={setSelectedTables}
                                selectionEnabled={selectionEnabled}
                                filteredTables={filteredTables}
                                editMode={editMode}
                                mapPositionOverrides={mapPositionOverrides}
                                setMapPositionOverrides={setMapPositionOverrides}
                            />
                        )}
                    </div>
                ),
            }}
        />
    );
}

function ListMode({ filteredTables, selectedTables, setSelectedTables, selectionEnabled }) {
    const { table, setTable, setLoading, setConfirm } = useContext(AppContext);

    const handleSelection = (tableId) => {
        const index = selectedTables.indexOf(tableId);
        if (index > -1) {
            const data = [...selectedTables];
            data.splice(index, 1);
            setSelectedTables(data);
        } else {
            setSelectedTables([...selectedTables, tableId]);
        }
    };

    const getClassName = (t, className = "") => {
        if (t.id == table.id) className += " visible";
        if (t.status == 1) className += " open";
        if (t.status == 2) className += " warning";
        if (selectedTables.includes(t.id)) className += " selected";
        if (selectionEnabled && t.childs.length > 0) className += " disabled";
        return className;
    };

    const getDuration = (dateStart) => {
        const ds = moment(dateStart, "YYYY-MM-DD HH:mm:ss");
        const de = moment();
        const diff = moment.duration(ds.diff(de));
        return diff.humanize(true);
    };

    const getBadgeLabel = (currentTable) => {
        if (Number(currentTable?.status) === 2) return "Attesa conto";
        if (currentTable?.date_open) return getDuration(currentTable.date_open);
        return "";
    };

    const unparentTable = async (table) => {
        console.log("unparentTable");
        setConfirm({
            title: "Sei sicuro?",
            message: "Il tavolo " + table.name + " verrà separato",
            onConfirm: async () => {
                setLoading(true);
                const res = await api.postProtected("/tables/edit/" + table.id + "/", {
                    parent_id: null,
                });
                setLoading(false);
                if (res.success == 0) alert(res.error);
            },
        });
    };

    return (
        <div className="tables-sidebar-container tables-sidebar">
            <div className="tables">
                {filteredTables.map((table, i) => (
                    <button
                        key={i}
                        onClick={() => {
                            if (selectionEnabled) {
                                if (table.childs.length == 0) handleSelection(table.id);
                            } else setTable(table);
                        }}
                        className={getClassName(table)}
                    >
                        <div className="table-main-column">
                            <div className="name">
                                {table.name}

                                {table.childs.map((child, n) => (
                                    <span key={n}>
                                        {" + " + child.name}
                                        <button
                                            onClick={() => unparentTable(child)}
                                            className="inline-button"
                                        >
                                            <TrashIcon />
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <div className="table-details-grid">
                                <div className="table-details-column">
                                    <div className="list-table">
                                        {"Listino: " + (table.list_id ? table.list?.name : "Base")}
                                    </div>
                                    <div>Ordine singolo: {table.single_order_mode == 1 ? "Si" : "No"}</div>
                                    <div className="diner">
                                        <UserIcon />
                                        Coperti: {table.covers}/{table.availables_covers}
                                    </div>
                                </div>
                                <div className="table-details-column">
                                    {table.pin && (
                                        <div className="pin-table">
                                            <span>PIN:</span> <strong>{table.pin}</strong>
                                        </div>
                                    )}
                                    <div className="flow">
                                        <Dish />
                                        <div>{"Ordini: " + (table.orders_count || 0)}</div>
                                    </div>
                                    {table.single_order_mode == 0 && (
                                        <div>
                                            <div>
                                                Portata: {table.flow}/{table.total_flows}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="table-details-column table-details-total-column">
                                    <div className="inline-total">
                                        {functions.formatter.format(table.orders_total)}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {getBadgeLabel(table) && <div className="badge">{getBadgeLabel(table)}</div>}
                    </button>
                ))}
            </div>
            <SidebarContent />
        </div>
    );
}

function MapMode2({
    filteredTables,
    selectedTables,
    setSelectedTables,
    selectionEnabled,
    editMode,
    mapPositionOverrides,
    setMapPositionOverrides,
}) {
    const {
        apiSettings,
        tables,
        rooms,
        shop,
        setTablePopup,
        table,
        setTable,
        editTable,
        setLoading,
        setConfirm,
    } = useContext(AppContext);
    function startDrag(e, id) {
        if (!editMode) return;
        e.preventDefault();
        const container = document.querySelector(".tables-board");
        const el = e.currentTarget.closest(".table-container");
        if (!container || !el) return;
        const point = document.createElement("div");
        point.classList.add("snap-point");
        container.appendChild(point);
        el.classList.add("dragging");

        const handleMove = (event) => drag(event, container, el, point);
        const handleUp = (event) => stopDrag(event, el, id, point, handleMove, handleUp);

        document.addEventListener("pointermove", handleMove);
        document.addEventListener("pointerup", handleUp);
        document.addEventListener("pointercancel", handleUp);
    }

    function getEffectiveTablePosition(currentTable) {
        return (
            getTableMapPosition(currentTable, mapPositionOverrides) ||
            fallbackPositions[currentTable.id] ||
            null
        );
    }

    function stopDrag(e, el, id, point, handleMove, handleUp) {
        e.preventDefault();
        point.remove();
        document.removeEventListener("pointermove", handleMove);
        document.removeEventListener("pointerup", handleUp);
        document.removeEventListener("pointercancel", handleUp);
        el.classList.remove("dragging");
        snap(el);
        const position = {
            top: el.style.top,
            left: el.style.left,
        };

        const draggedTable = filteredTables.find((currentTable) => currentTable.id == id);
        const originalPosition = draggedTable ? getEffectiveTablePosition(draggedTable) : null;

        const targetCol = getGridColumn(position.left);
        const targetRow = getGridRow(position.top);
        const occupyingTable = filteredTables.find((currentTable) => {
            if (currentTable.id == id) return false;
            const currentPosition = getEffectiveTablePosition(currentTable);
            if (!currentPosition) return false;
            return (
                getGridColumn(currentPosition.left) === targetCol &&
                getGridRow(currentPosition.top) === targetRow
            );
        });

        if (occupyingTable && originalPosition) {
            setMapPositionOverrides((currentPositions) => ({
                ...currentPositions,
                [id]: position,
                [occupyingTable.id]: originalPosition,
            }));
            editTable(id, { pos_y: position.top, pos_x: position.left });
            editTable(occupyingTable.id, { pos_y: originalPosition.top, pos_x: originalPosition.left });
        } else {
            setMapPositionOverrides((currentPositions) => ({
                ...currentPositions,
                [id]: position,
            }));
            editTable(id, { pos_y: position.top, pos_x: position.left });
        }
    }

    function getClientPoint(event) {
        if (Number.isFinite(event?.clientX) && Number.isFinite(event?.clientY)) return event;
        if (event?.touches?.[0]) return event.touches[0];
        if (event?.changedTouches?.[0]) return event.changedTouches[0];
        return { clientX: 0, clientY: 0 };
    }

    function drag(e, container, el, point) {
        e.preventDefault();
        const clientPoint = getClientPoint(e);
        const popupContent = document.querySelector(".inner-content");
        let newY = clientPoint.clientY - container.offsetTop + (popupContent?.scrollTop || 0);
        let newX = clientPoint.clientX - container.offsetLeft;
        if (newY <= 0) newY = 0;
        if (newX <= 0) newX = 0;
        if (newY + el.offsetHeight >= container.offsetHeight) newY = container.offsetHeight - el.offsetHeight;
        if (newX + el.offsetWidth >= container.offsetWidth) newX = container.offsetWidth - el.offsetWidth;
        const yPerc = (newY * 100) / container.offsetHeight;
        const xPerc = (newX * 100) / container.offsetWidth;
        el.style.top = yPerc + "%";
        el.style.left = xPerc + "%";
        drowSnapPoint(el, point);
    }

    function snap(el) {
        const top = parseFloat(el.style.top);
        const left = parseFloat(el.style.left);
        const snapped = snapToGridCell(left, top);
        el.style.top = snapped.top;
        el.style.left = snapped.left;
    }

    function drowSnapPoint(el, point) {
        const top = parseFloat(el.style.top);
        const left = parseFloat(el.style.left);
        const snapped = snapToGridCell(left, top);
        point.style.top = snapped.top;
        point.style.left = snapped.left;
    }

    const handleSelection = (tableId) => {
        const index = selectedTables.indexOf(tableId);
        if (index > -1) {
            const data = [...selectedTables];
            data.splice(index, 1);
            setSelectedTables(data);
        } else {
            setSelectedTables([...selectedTables, tableId]);
        }
    };

    const fallbackPositions = useMemo(
        () => getFallbackPositionMap(filteredTables, mapPositionOverrides),
        [filteredTables, mapPositionOverrides],
    );
    const fallbackRows = Math.ceil(Object.keys(fallbackPositions).length / MAP_GRID_MAX_COLUMNS);
    const boardStyle =
        fallbackRows > 0
            ? {
                  minHeight:
                      Math.max(MAP_FALLBACK_GRID_MIN_HEIGHT, fallbackRows * MAP_FALLBACK_GRID_ROW_HEIGHT) +
                      "px",
              }
            : undefined;

    const getClassName = (t, className = "") => {
        if (t.id == table.id) className += " visible";
        if (t.status == 1) className += " open";
        if (t.status == 2) className += " warning";
        if (selectedTables.includes(t.id)) className += " selected";
        if (selectionEnabled && t.childs.length > 0) className += " disabled";
        return className;
    };

    const getDuration = (dateStart) => {
        const ds = moment(dateStart, "YYYY-MM-DD HH:mm:ss");
        const de = moment();
        const diff = moment.duration(ds.diff(de));
        return diff.humanize(true);
    };

    const getBadgeLabel = (currentTable) => {
        if (Number(currentTable?.status) === 2) return "Attesa conto";
        if (currentTable?.date_open) return getDuration(currentTable.date_open);
        return "";
    };

    const unparentTable = async (table) => {
        console.log("unparentTable");
        setConfirm({
            title: "Sei sicuro?",
            message: "Il tavolo " + table.name + " verrà separato",
            onConfirm: async () => {
                setLoading(true);
                const res = await api.postProtected("/tables/edit/" + table.id + "/", {
                    parent_id: null,
                });
                setLoading(false);
                if (res.success == 0) alert(res.error);
            },
        });
    };

    const handleClick = (table) => {
        if (!editMode) {
            if (selectionEnabled) {
                if (table.childs.length == 0) handleSelection(table.id);
            } else {
                setTable(table);
                setTablePopup(true);
            }
        }
    };

    return (
        <div className="tables-board" style={boardStyle}>
            {filteredTables.map((table, i) => {
                const tablePosition =
                    getTableMapPosition(table, mapPositionOverrides) || fallbackPositions[table.id];
                const mapInfoItems = [
                    {
                        key: "covers",
                        icon: <UserIcon />,
                        value:
                            table.covers || table.availables_covers
                                ? `${table.covers}/${table.availables_covers}`
                                : null,
                    },
                    table.orders_total > 0
                        ? {
                              key: "total",
                              value: functions.formatter.format(table.orders_total),
                              className: "total",
                          }
                        : null,
                ].filter(Boolean);

                return (
                    <div
                        className="table-container size-1"
                        key={i}
                        style={{
                            top: tablePosition.top,
                            left: tablePosition.left,
                            width: MAP_TABLE_WIDTH_PERCENT + "%",
                        }}
                    >
                        <div
                            className={getClassName(table, "table" + (editMode ? " editable" : ""))}
                            onPointerDown={(e) => startDrag(e, table.id)}
                        >
                            <div className="header-tables">
                                <div className="name" onClick={() => handleClick(table)}>
                                    {table.name}
                                </div>
                                <div></div>
                                {table.childs.map((child, n) => (
                                    <Fragment key={n}>
                                        <div className="name" onClick={() => handleClick(table)}>
                                            {child.name}
                                        </div>
                                        <div>
                                            <button
                                                onClick={() => unparentTable(child)}
                                                className="inline-button"
                                            >
                                                <TrashIcon />
                                            </button>
                                        </div>
                                    </Fragment>
                                ))}
                            </div>
                            <div onClick={() => handleClick(table)} className="map-table-content">
                                {mapInfoItems.length > 0 && (
                                    <div className="map-table-info-list">
                                        {mapInfoItems.map((item) => (
                                            <div
                                                key={item.key}
                                                className={
                                                    "map-table-info-item" +
                                                    (item.className ? " " + item.className : "")
                                                }
                                            >
                                                {item.icon ? item.icon : null}
                                                <div>{item.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {getBadgeLabel(table) && <div className="badge map">{getBadgeLabel(table)}</div>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default TablesList;
